import React, { CSSProperties } from 'react'
import { connect } from 'react-redux'
import { RouteComponentProps } from 'react-router'
import { withRouter } from 'react-router-dom'
import { TidewaysContext, ContextRoute } from './app/context'
import { Operation } from './domain/Operation'
import {
    DifferentialFlamegraphNode,
    diffFlamegraphs,
    FlamegraphNode,
    SimpleTrace,
    Trace,
    TraceDiffItem,
    traceHasCallgraphData,
    traceSpanCategories,
} from './domain/Trace'
import { TraceDiffTranslation, traceIsOlder, TraceSummarySingleView } from './domain/TraceSummary'
import Link from './helper/link'
import Loading from './helper/loading'
import { PortalContentNavigation } from './helper/portal-content-navigation'
import { ApplicationState } from './redux/ApplicationState'
import { fetchTrace } from './redux/V2/Trace/actions'
import { getTrace, isTraceLoaded } from './redux/V2/Trace/TraceState'
import Router from './router'
import { Callgraph } from './trace/callgraph'
import Diff from './trace/diff/Diff'
import TraceOverview from './trace/overview'
import { Container } from './ui/grid'
import { xhprofCompactGraph } from './xhprof/XhprofCompactGraph'
import { compareXhprofRawData } from './xhprof/XhprofCompareRawData'
import { generateXhprofReport } from './xhprof/XhprofReportGenerator'
import ShareTraceForm from './monitor/operation/share-trace/share-trace-form'
import { Entity } from './app/entity'
import { FlamegraphComponent } from './trace/flamegraph'
import { parseQueryString } from './helper/queryString'
import { TimelineSpan } from './domain/TimelineSpan'

interface MatchParameters {
    profile: string
    base: string
}

interface OwnProps {
    view?: 'callgraph' | 'timeline' | 'flamegraph' | 'summary'
}

interface DispatchProps {
    fetchTrace: (id: string) => void
}

interface StateProps {
    parameters: { [key: string]: string }
    tidewaysContext: TidewaysContext
    router: Router
    route: ContextRoute
    profileBase?: TraceSummarySingleView | null
    profileTarget?: TraceSummarySingleView | null
    traceBase: SimpleTrace | null
    traceTarget: SimpleTrace | null
    traceBaseId: string
    traceTargetId: string
    traceBaseLoaded: boolean
    traceTargetLoaded: boolean
    flamegraphBase?: { spans: TimelineSpan[]; flamegraph: FlamegraphNode }
    flamegraphTarget?: { spans: TimelineSpan[]; flamegraph: FlamegraphNode }
    includesDynamicallyInstrumentedFunctions: boolean
}

type Props = OwnProps & StateProps & DispatchProps & RouteComponentProps<MatchParameters>

interface State {
    baseProfile: TraceSummarySingleView | null
    targetProfile: TraceSummarySingleView | null
    hasCallgraph: boolean
    operation?: Operation
    diff?: TraceDiffItem[]
    shareTrace: boolean
}

export class TraceDiff extends React.Component<Props, State> {
    public state: State = {
        baseProfile: null,
        targetProfile: null,
        hasCallgraph: false,
        shareTrace: false,
    }

    public componentDidMount() {
        this.props.router.setDefaultParameter('profile', this.props.parameters.profile)
        this.props.router.setDefaultParameter('base', this.props.parameters.base)
        this.fetchTrace()
        this.reload()
    }

    public componentDidUpdate(prevProps) {
        if (prevProps.parameters.profile !== this.props.parameters.profile || prevProps.parameters.base !== this.props.parameters.base) {
            this.props.router.setDefaultParameter('profile', this.props.parameters.profile)
            this.props.router.setDefaultParameter('base', this.props.parameters.base)
            this.fetchTrace()
            this.reload()
        }
    }

    public fetchTrace = () => {
        if (!this.props.traceBaseLoaded) {
            this.props.fetchTrace(this.props.traceBaseId)
        }

        if (!this.props.traceTargetLoaded) {
            this.props.fetchTrace(this.props.traceTargetId)
        }
    }

    public reload = () => {
        jQuery.ajax({
            url: this.props.router.path('XhprofProfilerBundle.Profile.compareData', this.props.parameters),
            success: data => {
                this.setState(data)
            },
        })
    }

    public render() {
        const active = this.props.view
        const { baseProfile, targetProfile } = this.state

        if (!baseProfile || !targetProfile || !this.props.traceBaseLoaded || !this.props.traceTargetLoaded) {
            return <Loading />
        }

        const comparisonIsShared = !!(this.props.profileBase?.hasActiveShare && this.props.profileTarget?.hasActiveShare)
        const sharingStatus = comparisonIsShared ? 'Is Shared' : 'Share'
        const showShareTraceForm =
            this.state.shareTrace && this.props.traceTarget && this.props.traceBase && this.props.profileBase && this.props.profileTarget
        let diffTrace: Trace | null = null
        let diffFlamegraph: DifferentialFlamegraphNode | null = null
        let compareCallgraphs = false

        if (this.props.traceTargetLoaded && this.props.traceBaseLoaded && this.props.traceBase && this.props.traceTarget) {
            const diffCompareRawData = compareXhprofRawData(this.props.traceBase.rawData, this.props.traceTarget.rawData)
            if (this.props.flamegraphBase && this.props.flamegraphTarget) {
                diffFlamegraph = diffFlamegraphs(this.props.flamegraphTarget.flamegraph, this.props.flamegraphBase.flamegraph)
            }

            compareCallgraphs = traceHasCallgraphData(this.props.traceBase) && traceHasCallgraphData(this.props.traceTarget)

            const reportDiff = generateXhprofReport(diffCompareRawData)
            const reportCompact = compareCallgraphs ? { ...reportDiff, data: xhprofCompactGraph(reportDiff.data, 95) } : null

            diffTrace = {
                id: 'diff-trace',
                traceId: 1234,
                time: 1234,
                transaction: '',
                rawData: diffCompareRawData,
                xhprofReport: reportDiff,
                xhprofReportCompact: reportCompact,
                spans: [],
                globalEvents: [],
                ticks: {},
                hidden: 0,
                memoryLimit: false,
                logs: [],
                parentSpanId: null,
                server: this.props.traceBase.server,
                service: this.props.traceBase.service,
                environment: this.props.traceBase.environment,
                measureDate: this.props.traceBase.measureDate,
                flamegraph: {
                    id: '1',
                    name: 'root',
                    title: 'root',
                    values: { mc: 0, wt: 0 },
                    children: [],
                    cat: 'app',
                    customType: null,
                    stats: { ct: 1, mc: 0, wt: 0 },
                    stack: null,
                },
            }
        }

        const compareButtonPosition: CSSProperties = { position: 'absolute', marginLeft: 20, marginTop: -100 }

        const traceDiffTranslation: TraceDiffTranslation = {
            from: traceIsOlder(baseProfile, targetProfile) ? 'Before' : 'After',
            to: traceIsOlder(targetProfile, baseProfile) ? 'Before' : 'After',
        }

        return (
            <div>
                <PortalContentNavigation>
                    <div className="clearfix" style={{ background: 'white' }}>
                        <Container>
                            <div className="tw-pl-32">
                                <TraceOverview prefix={traceDiffTranslation.from} profile={baseProfile} operation={this.state.operation} />
                                <TraceOverview prefix={traceDiffTranslation.to} profile={targetProfile} operation={this.state.operation} />
                            </div>

                            <div style={compareButtonPosition}>
                                <Link
                                    className="btn btn-default"
                                    params={{ profile: this.props.traceBaseId, base: this.props.traceTargetId }}
                                >
                                    <span className="fa fa-exchange" />
                                </Link>
                            </div>
                        </Container>
                    </div>
                </PortalContentNavigation>

                {showShareTraceForm ? (
                    <ShareTraceForm
                        trace={this.props.traceTarget}
                        baseTrace={this.props.traceBase}
                        onExit={() => this.setState({ shareTrace: false })}
                        traceSummary={this.props.profileTarget}
                        baseTraceSummary={this.props.profileBase}
                        hasActiveShare={comparisonIsShared}
                        allowedToShare={this.props.tidewaysContext.allowedToShare}
                        defaultRetentionDays={this.props.tidewaysContext.application.retentionDays}
                    />
                ) : (
                    <React.Fragment>
                        <div className="row">
                            <div className="col-lg-8 col-md-12">
                                <ul className="nav nav-pills nav-pills-tideways">
                                    <li className={active === 'flamegraph' ? 'active' : ''}>
                                        <Link route="XhprofProfilerBundle.Profile.compare">Flamegraph</Link>
                                    </li>
                                    <li className={active === 'timeline' || active === 'summary' ? 'active' : ''}>
                                        <Link route="XhprofProfilerBundle.Profile.summaryCompare">Summary</Link>
                                    </li>
                                    {compareCallgraphs ? (
                                        <li className={active === 'callgraph' ? 'active' : ''}>
                                            <Link route="XhprofProfilerBundle.Profile.callgraphCompare">Callgraph</Link>
                                        </li>
                                    ) : null}
                                </ul>
                            </div>
                            <div className="col-md-4">
                                <div className="pull-right" style={{ marginLeft: '1em' }}>
                                    <a
                                        className="btn btn-default"
                                        onClick={() => {
                                            this.setState({ shareTrace: true })
                                        }}
                                    >
                                        <i className="fa fa-share" /> {sharingStatus}
                                    </a>
                                </div>
                            </div>
                        </div>
                        {(() => {
                            const queryParameters = parseQueryString(this.props.location.search)
                            switch (active) {
                                case 'timeline':
                                case 'summary':
                                    return (
                                        <Diff
                                            diff={this.state.diff}
                                            base={baseProfile}
                                            target={targetProfile}
                                            traceDiffTranslation={traceDiffTranslation}
                                        />
                                    )
                                case 'flamegraph':
                                    return this.props.flamegraphBase && diffFlamegraph ? (
                                        <FlamegraphComponent
                                            route="XhprofProfilerBundle.Profile.flamegraphCompare"
                                            spans={this.props.flamegraphBase.spans}
                                            data={diffFlamegraph}
                                            categories={traceSpanCategories}
                                            router={this.props.router}
                                            params={queryParameters}
                                            isAggregatedTrace={false}
                                            differentialMode
                                            layers={{ base: baseProfile.layers, target: targetProfile.layers }}
                                            traceDiffTranslation={traceDiffTranslation}
                                            switchComparisonOrder={() => {
                                                this.props.router.push(this.props.route.route, {
                                                    ...this.props.parameters,
                                                    profile: this.props.traceBaseId,
                                                    base: this.props.traceTargetId,
                                                })
                                            }}
                                        />
                                    ) : null
                                case 'callgraph':
                                    return diffTrace ? (
                                        <Callgraph
                                            startTracepointEvent={null}
                                            trace={diffTrace}
                                            router={this.props.router}
                                            url={this.props.router.path(
                                                'XhprofProfilerBundle.Profile.callgraphCompareData',
                                                this.props.parameters,
                                            )}
                                            clickRoute="XhprofProfilerBundle.Profile.callgraphCompare"
                                            params={this.props.parameters}
                                            showPlusWhenPositive
                                            includesDynamicallyInstrumentedFunctions={this.props.includesDynamicallyInstrumentedFunctions}
                                            bottlenecks={[]}
                                        />
                                    ) : null
                            }
                        })()}
                    </React.Fragment>
                )}
            </div>
        )
    }
}

export default withRouter(
    connect<StateProps, DispatchProps, OwnProps>(
        (globalState: ApplicationState, props: RouteComponentProps<MatchParameters> & DispatchProps & OwnProps) => {
            const traceBaseId = props.match.params.base
            const traceTargetId = props.match.params.profile
            const traceBaseOld = globalState.traces.traces[traceBaseId] || new Entity()
            const traceTargetOld = globalState.traces.traces[traceTargetId] || new Entity()

            return {
                ...props,
                tidewaysContext: globalState.tidewaysContext,
                parameters: globalState.tidewaysContext.route.parameters,
                router: globalState.tidewaysContext.router,
                route: globalState.tidewaysContext.route,
                traceBase: getTrace(globalState, traceBaseId),
                traceTarget: getTrace(globalState, traceTargetId),
                traceBaseId,
                traceTargetId,
                traceTargetLoaded: isTraceLoaded(globalState, traceTargetId),
                traceBaseLoaded: isTraceLoaded(globalState, traceBaseId),
                profileBase: traceBaseOld.data?.profile,
                profileTarget: traceTargetOld.data?.profile,
                flamegraphBase: traceBaseOld.data
                    ? { spans: traceBaseOld.data.traces[0].spans, flamegraph: traceBaseOld.data.traces[0].flamegraph }
                    : undefined,
                flamegraphTarget: traceTargetOld.data
                    ? { spans: traceTargetOld.data.traces[0].spans, flamegraph: traceTargetOld.data.traces[0].flamegraph }
                    : undefined,
                includesDynamicallyInstrumentedFunctions: globalState.tidewaysContext.application.includesDynamicallyInstrumentedFunctions,
            }
        },
        { fetchTrace },
    )(TraceDiff),
)
